/**
 * Client-side platform detection for the Web → App invite (PB-303).
 *
 * Two jobs:
 *   1. Pick the right CTA — iOS/Android mobile web get the matching store badge,
 *      desktop gets a QR code (a dead store link is useless on a machine that
 *      can't install the app).
 *   2. Hard-suppress the whole surface inside the native app — it's a web-only
 *      prompt (see the card's "Never render inside the native app").
 *
 * All reads are guarded for SSR (`typeof window`/`navigator`) because this is
 * imported by a client component that still renders on the server first.
 */

export type WebPlatform = 'ios' | 'android' | 'desktop'

/**
 * Coarse OS bucket from a user-agent STRING — the one UA rule, usable where
 * there is no `navigator`: middleware reads it off the request header to send a
 * `/m/{code}` scanner to their own store (see `proxy.ts`).
 *
 * Cannot see the iPadOS case on its own: iPadOS 13+ reports as desktop Safari
 * and is only distinguishable by touch capability, which is a DOM read. A
 * server caller therefore buckets an iPad as 'desktop' — deliberate, since the
 * alternative is sniffing an iPad by UA alone, which is not possible.
 */
export function platformFromUserAgent(userAgent: string | null | undefined): WebPlatform {
  const ua = userAgent ?? ''

  if (/android/i.test(ua)) return 'android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'

  return 'desktop'
}

/**
 * Whether a user-agent STRING is a Mac's: the one desktop Loop runs on, so the
 * one an Invite Link page offers to open Loop on (ADR-0158). An iPad passes too,
 * since iPadOS reports desktop Safari; on the client `detectPlatform()` tells
 * the two apart.
 */
export function isMacUserAgent(userAgent: string | null | undefined): boolean {
  return platformFromUserAgent(userAgent) === 'desktop' && /Macintosh/i.test(userAgent ?? '')
}

/**
 * Whether a user-agent string is a Chromium browser's (Chrome, Brave, Edge,
 * Arc, Opera — all carry `Chrome/`; Safari and Firefox do not). Chromium does
 * nothing visible for a URL scheme no installed app claims, which makes it the
 * one family a page may hand a scheme to unprompted (ADR-0158).
 */
export function isChromiumUserAgent(userAgent: string | null | undefined): boolean {
  return /\bChrome\//.test(userAgent ?? '')
}

/**
 * Coarse OS bucket for the current client. Adds the one thing a UA string
 * cannot carry: iPadOS 13+ reports as desktop Safari, so a touch-capable
 * "Macintosh" is treated as iOS to route iPads to the App Store.
 */
export function detectPlatform(): WebPlatform {
  if (typeof navigator === 'undefined') return 'desktop'

  const ua = navigator.userAgent || ''

  const fromUserAgent = platformFromUserAgent(ua)
  if (fromUserAgent !== 'desktop') return fromUserAgent

  const iPadOS = /Macintosh/i.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document
  return iPadOS ? 'ios' : 'desktop'
}

export function isMobileWeb(): boolean {
  return detectPlatform() !== 'desktop'
}

/**
 * True when the web app is running *inside* the HubHive native app's webview.
 *
 * The native app does not currently host the full web shell in a webview (it
 * only uses webviews for isolated screens like business reports), so this is
 * defensive: we detect the JS bridges an in-app webview would expose, plus an
 * explicit `window.HubHiveApp` flag the app can inject if it ever embeds the
 * shell. When true, the invite never renders — it's a web-only surface.
 */
export function isNativeAppClient(): boolean {
  if (typeof window === 'undefined') return false

  const w = window as unknown as {
    ReactNativeWebView?: unknown
    flutter_inappwebview?: unknown
    HubHiveApp?: unknown
  }

  return Boolean(w.ReactNativeWebView || w.flutter_inappwebview || w.HubHiveApp)
}
